import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { ShieldCheck, ArrowRight, Globe } from "lucide-react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're subscribed. Watch your inbox for tax-saving tips.");
      setEmail("");
    } catch {
      toast.error("Couldn't subscribe right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="border-t border-border bg-card mt-24" data-testid="footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="grid h-10 w-10 place-items-center rounded-md bg-primary text-primary-foreground font-display font-bold">R</div>
              <div className="leading-tight">
                <div className="font-display text-lg font-bold tracking-tight">CA Ritika Singh</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">AI Tax Filing</div>
              </div>
            </Link>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-muted-foreground">
              Simple. Accurate. AI-powered Income Tax Return filing for India — built by Chartered Accountants, trusted by salaried professionals, founders and freelancers across 280+ cities.
            </p>
            <form onSubmit={subscribe} className="mt-6 flex w-full max-w-md gap-2" data-testid="newsletter-form">
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="you@example.com"
                aria-label="Email for tax newsletter"
                data-testid="newsletter-email"
                className="h-11"
                required
              />
              <Button data-testid="newsletter-submit" type="submit" disabled={loading} className="h-11">
                {loading ? "…" : <>Subscribe<ArrowRight className="ml-1 h-4 w-4" /></>}
              </Button>
            </form>
            <div className="mt-5 flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-accent" />
              <span>256-bit AES encryption · DPDP & GDPR compliant · ISO 27001 ready</span>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-foreground">Product</div>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li><Link to="/pricing" className="hover:text-foreground">Pricing</Link></li>
              <li><Link to="/calculator" className="hover:text-foreground">Tax Calculator</Link></li>
              <li><Link to="/knowledge" className="hover:text-foreground">Knowledge Center</Link></li>
              <li><Link to="/contact" className="hover:text-foreground">Book a CA</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-foreground">Filing</div>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li>ITR-1 (Salary)</li>
              <li>ITR-2 (Capital Gains)</li>
              <li>ITR-3 (Business)</li>
              <li>ITR-4 (Presumptive)</li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-foreground">Company</div>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li><Link to="/about" className="hover:text-foreground">About CA Ritika Singh</Link></li>
              <li><Link to="/contact" className="hover:text-foreground">Contact</Link></li>
              <li className="text-xs">Disclaimer: Independent CA firm. Not affiliated with the Income Tax Department of India.</li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 border-t border-border pt-6 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} CA Ritika Singh. All rights reserved.</div>
          <div className="flex items-center gap-2"><Globe className="h-3.5 w-3.5" /> Made in India · For India</div>
        </div>
      </div>
    </footer>
  );
}
